import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import Text from "../../../Inputs/Text";
import Field from "../../../Field";
import Select from "../../../Inputs/Select";
import classNames from "classnames";

const getFieldMethod = (type) => {
	switch (type) {
		case "text":
			return "contains";
		case "resource-select":
		case "select":
			return "exact";
		default:
			return "exact";
	}
};

function Filter({
	changeFieldMethod,
	changeFieldValue,
	config,
	_config,
	name,
	add,
	remove,
}) {
	const { t } = useTranslation();
	const list = Form.useWatch("filters");
	const current = useMemo(() => list?.[name], [name, list]);
	const [search, setSearch] = useState("");

	const { type, disableFilters } = useMemo(() => {
		return config?.find((configItem) => configItem.name === current?.name) || {};
	}, [config, current?.name]);

	const fields = useMemo(() => {
		return _config
			?.filter(({ name }) => {
				return (
					!(list?.findIndex((item) => item.name === name) !== -1) ||
					current?.name === name
				);
			})
			.map(({ name, label, children }) => {
				const value = { name, label };
				if (children) {
					value.children = children.filter((child) => {
						const _name = `${name}.${child.name}`;
						return !(list?.findIndex((item) => item.name === _name) !== -1) ||
							current?.name === _name;
					});
				}
				return value;
			});
	}, [_config, list, current]);

	const input = useMemo(() => {
		const itemConfig = config?.find(
			(configItem) => configItem.name === current?.name,
		) || {};
		const { component, disableFilters } = itemConfig;

		if (component) {
			if (!isValidElement(component)) {
				return component;
			}
			if (component.props.disabled !== undefined) {
				return component;
			}
			return cloneElement(
				component,
				{ disabled: disableFilters },
			);
		}
		return (
			<Text
				className="form-control-sm px-4"
				type="text"
				placeholder={t("value")}
				disabled={disableFilters}
			/>
		);
	}, [config, current?.name, t]);

	const onFieldChange = useCallback(
		(fieldName) => {
			setSearch("");
			const type = config?.find(
				(configItem) => configItem.name === fieldName,
			)?.type;
			const method = getFieldMethod(type);
			changeFieldMethod(name, method);
			changeFieldValue(name, undefined);
		},
		[config, name, changeFieldMethod, changeFieldValue],
	);

	useEffect(() => {
		const method = getFieldMethod(type);
		changeFieldMethod(name, method);
	}, [name, type, changeFieldMethod]);

	return (
		<div className="d-flex justify-content-end align-items-center">
			<div style={{ width: 210 }}>
				<Field
					name={[name, "name"]}
					className="mb-0 mr-2"
				>
					<Select
						placeholder={t("field")}
						onChange={onFieldChange}
						disabled={disableFilters}
						showSearch
						filterOption={false}
						searchValue={search}
						onSearch={(e) => setSearch(e)}
					>
						{fields
							.filter(({ name, label }) => {
								const formattedSearch = search.toLowerCase().trim();
								const formattedItem = (label || name).toLowerCase().trim();

								return formattedItem.includes(formattedSearch);
							})
							.map(({ name, label, children }) => {
								if (children) {
									if (!children.length) {
										return null;
									}
									return (
										<Select.OptGroup key={name} label={t(label) || t(name)}>
											{children.map((child) => (
												<Select.Option key={`${name}.${child.name}`} value={`${name}.${child.name}`}>
													{t(child.label) || t(child.name)}
												</Select.Option>
											))}
										</Select.OptGroup>
									);
								}
								return (
									<Select.Option key={name} value={name}>
										{t(label) || t(name)}
									</Select.Option>
								);
							})}
					</Select>
				</Field>
			</div>

			<BaseField name={[name, "method"]} hidden />

			<div style={{ width: 210 }}>
				<Field name={[name, "value"]} className="mb-0 mr-2 w-100">
					{input}
				</Field>
			</div>

			<span className="mr-1" style={{ height: 25, width: 25 }}>
				{(list?.length < config.length &&
					list?.length < 3
				) && (
						<i
							className="ni ni-fat-add text-info"
							style={{ fontSize: "25px", cursor: "pointer" }}
							onClick={add}
						/>
					)}
			</span>

			<span style={{ height: 25, width: 25 }}>
				{list?.length > 1 && (
					<i
						className={classNames("ni ni-fat-delete", disableFilters ? "text-gray" : "text-info")}
						style={{ fontSize: "25px", cursor: disableFilters ? "initial" : "pointer" }}
						onClick={disableFilters ? undefined : remove}
					/>
				)}
			</span>
		</div>
	);
}

export default Filter;
